import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { isAndroidApp } from "@/utils/platform";

interface PremiumContextType {
  isPremium: boolean;
  loading: boolean;
  /** Active subscription expiry date, if any */
  expiresAt: string | null;
  /** Force refresh premium status from DB */
  refresh: () => Promise<void>;
}

const PremiumContext = createContext<PremiumContextType>({
  isPremium: false,
  loading: true,
  expiresAt: null,
  refresh: async () => {},
});

/**
 * Premium status is computed STRICTLY from the subscriptions table:
 *   status = 'active' AND expires_at > now()
 * Never from localStorage or in-memory flags.
 * This ensures persistence across logout/login for email + anonymous users.
 */
export const PremiumProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [isPremium, setIsPremium] = useState(false);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchPremiumStatus = useCallback(async () => {
    // In-app (Play Store / PWA wrapper) users get premium unlocked automatically
    if (isAndroidApp()) {
      setIsPremium(true);
      setExpiresAt(null);
      setLoading(false);
      return;
    }

    if (!user) {
      setIsPremium(false);
      setExpiresAt(null);
      setLoading(false);
      return;
    }

    try {
      // Check for active subscription in DB (source of truth)
      const { data: sub } = await supabase
        .from("subscriptions")
        .select("id, status, expires_at")
        .eq("user_id", user.id)
        .in("status", ["active", "grace_period"])
        .order("expires_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      const hasActiveSub = !!(
        sub &&
        sub.expires_at &&
        new Date(sub.expires_at) > new Date()
      );

      // Also check profiles.is_premium as fallback (webhook may have set it)
      const { data: profile } = await supabase
        .from("profiles")
        .select("is_premium")
        .eq("id", user.id)
        .single();

      const premium = hasActiveSub || (profile?.is_premium ?? false);

      // Sync profiles.is_premium if it's out of date
      if (hasActiveSub && !profile?.is_premium) {
        await supabase.from("profiles").update({ is_premium: true }).eq("id", user.id);
      } else if (!hasActiveSub && profile?.is_premium) {
        // Subscription expired but profile still says premium — fix it
        await supabase.from("profiles").update({ is_premium: false }).eq("id", user.id);
      }

      setIsPremium(premium);
      setExpiresAt(sub?.expires_at ?? null);
    } catch {
      console.error("Failed to check premium status");
      setIsPremium(false);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPremiumStatus();
  }, [fetchPremiumStatus]);

  return (
    <PremiumContext.Provider value={{ isPremium, loading, expiresAt, refresh: fetchPremiumStatus }}>
      {children}
    </PremiumContext.Provider>
  );
};

export const usePremiumContext = () => useContext(PremiumContext);
